import FluxUtils from 'flux/utils';
import { StaticContext } from 'react-router';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import AuthenticationActions from 'sharedreact/actions/AuthenticationActions';
import AuthenticationPage from 'sharedreact/components/AuthenticationPage';
import asFunctional from 'sharedreact/components/asFunctional';
import type Authentication from 'sharedreact/data/Authentication';
import AuthenticationStore from 'sharedreact/stores/AuthenticationStore';
import ErrorStore from 'sharedreact/stores/ErrorStore';
import SettingsStore from 'sharedreact/stores/SettingsStore';
import ConnectionSupport from 'sharedreact/utils/ConnectionSupport';
import { safeIdDecode } from 'sharedreact/utils/Encode';
import LocationState from 'sharedreact/utils/LocationState';
import { assertNonNullish } from 'sharedreact/utils/TypeSupport';
import UrlQuery from 'sharedreact/utils/UrlQuery';

function handleOnLoad (props: Props, oktaLoginCode: string, idpName: string, oktaLoginError: string, oktaLoginErrorDetail:string, referrer: string) {
  // Handle the Okta login
  if (oktaLoginCode) {
    if (referrer) {
      const referrerJson = JSON.parse(referrer);
      const location = props.location;
      if (location.state == null) {
        location.state = { 'referrer': referrerJson };
      } else {
        location.state.referrer = referrerJson;
      }
    }

    setTimeout(() => AuthenticationActions.loginWithOkta(oktaLoginCode, idpName, props.postLogin));
  } else if (oktaLoginError) {
    setTimeout(() => AuthenticationActions.signalOktaLoginFailure(oktaLoginError, oktaLoginErrorDetail));
  }

  setTimeout(() => {
    AuthenticationActions.getServerVersion();
    AuthenticationActions.getLogonMethods();
  });
}

function handleRemoveOktaParametersFromPath (props: Props): void {
  const search: {[key: string]: string} = UrlQuery.parse(props.location.search);
  if (search.code || search.state) {
    let newSearch = Object.assign({}, search);

    delete newSearch.code;
    delete newSearch.state;
    delete newSearch.error;
    delete newSearch.error_description;
    delete newSearch.session_state;

    props.history.push(props.location.pathname + UrlQuery.stringify(newSearch));
  }
}

function handleForgotPassword (authentication: Authentication, language: string) {
  setTimeout(() => AuthenticationActions.forgotPassword(authentication, language));
}

function handleResetPassword (authentication: Authentication, props: Props, onSuccess?: () => void) {
  const search: {[key: string]: string} = UrlQuery.parse(props.location.search);
  const identifier: string = search.idf ? search.idf : '';

  setTimeout(() => AuthenticationActions.resetPassword(authentication, identifier, onSuccess));
}

type Props = RouteComponentProps<{}, StaticContext, LocationState> & {
  postLogin?: () => void,
  postLogout?: () => void
};

type State = ConstructorParameters<typeof AuthenticationPage>[0];

function getStores () {
  return [
    AuthenticationStore,
    ErrorStore,
    SettingsStore
  ];
}

function calculateState (prevState?: State, props?: Props): State {
  assertNonNullish(props);

  const search: {[key: string]: string} = UrlQuery.parse(props.location.search);

  const oktaLoginCode: string = search.code ? search.code : '';
  const oktaLoginError: string = search.error ? search.error : '';
  const oktaLoginErrorDetail: string = search.error_description ? search.error_description : '';
  const encodedState: string | undefined = search.state;
  let referrer: string = '';
  let idpName: string = '';

  if (encodedState) {
    const decodedState: string = safeIdDecode(encodedState);
    const decodedStateJson: {[key: string]: any} = JSON.parse(decodedState);
    referrer = decodedStateJson['referrer'];
    
    idpName = decodedStateJson.idpName || '';
  }

  const showForgotPassword: boolean = search.action ? search.action === 'forgotpassword' : false;
  const showResetPassword: boolean = search.action ? search.action === 'resetpassword' : false;
  const language: string = SettingsStore.getLanguage();

  const hasFetchedServerVersion: boolean = AuthenticationStore.getServerVersion() !== '';

  return {
    location: props.location,
    language: language,
    authenticationMethods: AuthenticationStore.getAuthenticationMethods(),
    docUrl: (hasFetchedServerVersion
      ? ConnectionSupport.createDocumentationLink(language, AuthenticationStore.getServerVersion(), 'BrowserClient_HTTPS')
      : undefined
    ),
    oktaLoginCode: oktaLoginCode,
    idpName: idpName,
    oktaLoginError: oktaLoginError,
    oktaLoginErrorDetail: oktaLoginErrorDetail,
    referrer: referrer,
    hasError: ErrorStore.getErrors().size > 0,
    showForgotPassword: showForgotPassword,
    showResetPassword: showResetPassword,
    forgotPasswordMailHasBeenSent: AuthenticationStore.getState().forgotPasswordMailHasBeenSent,
    onLogin: (user: Authentication) => AuthenticationActions.login(user, props.postLogin),
    onLogout: () => AuthenticationActions.logout(props.postLogout),
    onLoad: (oktaLoginCode: string, idpName: string, oktaLoginError: string, oktaLoginErrorDetail: string, referrer: any) => handleOnLoad(props, oktaLoginCode, idpName, oktaLoginError, oktaLoginErrorDetail, referrer),
    onForgotPassword: (authentication: Authentication) => handleForgotPassword(authentication, language),
    onResetPassword: (authentication: Authentication, onSuccess?: () => void) => handleResetPassword(authentication, props, onSuccess),
    removeOktaParametersFromPath: () => handleRemoveOktaParametersFromPath(props),
    onGoBack: AuthenticationActions.clearForgotPasswordMailHasBeenSent
  };
}

export default withRouter(FluxUtils.Container.createFunctional(
  asFunctional(AuthenticationPage),
  getStores,
  calculateState,
  { withProps: true }
));
